import { API_BASE } from "../utils/api";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/Toast";

const CATEGORY_LABELS = {
  tax_return: "Tax Return",
  invoice: "Invoice",
  payslip: "Payslip",
  bank_statement: "Bank Statement",
  expense: "Expense",
  contract: "Contract",
  other: "Other",
};

const CATEGORY_PILL = {
  tax_return:    "bg-yellow-50    dark:bg-luxury-elevated text-yellow-700  dark:text-luxury-gold   border border-yellow-200  dark:border-luxury-gold/30",
  invoice:       "bg-amber-50     dark:bg-luxury-elevated text-amber-700   dark:text-yellow-400    border border-amber-200   dark:border-yellow-400/30",
  payslip:       "bg-emerald-50   dark:bg-luxury-elevated text-emerald-700 dark:text-luxury-cta    border border-emerald-200 dark:border-luxury-cta/30",
  bank_statement:"bg-purple-50    dark:bg-luxury-elevated text-purple-700  dark:text-purple-400    border border-purple-200  dark:border-purple-400/30",
  expense:       "bg-red-50       dark:bg-luxury-elevated text-red-700     dark:text-red-400       border border-red-200     dark:border-red-400/30",
  contract:      "bg-blue-50      dark:bg-luxury-elevated text-blue-700    dark:text-blue-400      border border-blue-200    dark:border-blue-400/30",
  other:         "bg-luxury-elevated text-luxury-secondary border border-luxury-border",
};

function formatFileSize(bytes) {
  if (!bytes) return "—";
  const k = 1024;
  const sizes = ["B", "KB", "MB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-ZA", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function FileIconBox({ fileType }) {
  const getStyle = () => {
    if (!fileType) return "bg-luxury-elevated text-luxury-muted";
    if (fileType.includes("pdf")) return "bg-red-900/30 text-red-400";
    if (fileType.includes("image")) return "bg-purple-900/30 text-purple-400";
    if (fileType.includes("word") || fileType.includes("document")) return "bg-luxury-elevated text-luxury-gold";
    if (fileType.includes("excel") || fileType.includes("sheet")) return "bg-green-900/30 text-green-400";
    return "bg-luxury-elevated text-luxury-muted";
  };

  return (
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${getStyle()}`}>
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    </div>
  );
}

export default function Documents() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const fetchDocs = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/documents/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDocuments(data.documents || []);
      }
    } catch (err) {
      console.error("Failed to fetch documents:", err);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchDocs(); }, [token]);

  const handleDelete = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    setDeletingId(docId);
    try {
      const res = await fetch(`${API_BASE}/api/documents/${docId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setDocuments((prev) => prev.filter((d) => d.id !== docId));
        addToast("Document deleted successfully", "success");
      }
    } catch (err) {
      addToast("Failed to delete document", "error");
    } finally {
      setDeletingId(null);
    }
  };

  const filteredDocs = documents.filter((doc) => {
    const matchSearch = !searchQuery ||
      doc.original_filename?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchCategory = !filterCategory || doc.category === filterCategory;
    return matchSearch && matchCategory;
  });

  return (
    <div className="min-h-screen bg-luxury-bg p-6 lg:p-8">

      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-7">
        <div>
          <h1 className="text-2xl font-bold text-luxury-text">My Documents</h1>
          <p className="text-sm mt-1 text-luxury-secondary">
            {loading ? "Loading..." : `${documents.length} document${documents.length !== 1 ? "s" : ""} in your vault`}
          </p>
        </div>
        <button
          onClick={() => navigate("/upload")}
          className="flex-shrink-0 inline-flex items-center gap-2 bg-luxury-cta hover:bg-luxury-cta-hover text-white font-semibold px-5 py-2.5 rounded-xl transition-colors text-sm active:scale-95"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Upload New
        </button>
      </div>

      {/* Category Quick Filters */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1">
        {[
          { label: "All", value: "" },
          { label: "Tax Return", value: "tax_return" },
          { label: "Invoice", value: "invoice" },
          { label: "Payslip", value: "payslip" },
          { label: "Bank Statement", value: "bank_statement" },
          { label: "Expense", value: "expense" },
          { label: "Contract", value: "contract" },
        ].map((tab) => (
          <button
            key={tab.value}
            onClick={() => setFilterCategory(tab.value)}
            className={`flex-shrink-0 px-4 py-2 rounded-xl text-xs font-semibold transition-colors ${
              filterCategory === tab.value
                ? "bg-luxury-cta text-white"
                : "bg-luxury-surface border border-luxury-border text-luxury-secondary hover:bg-luxury-elevated hover:text-luxury-text"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Documents Card */}
      <div className="rounded-2xl border border-luxury-border bg-luxury-surface overflow-hidden">
        {/* Search + filter bar */}
        <div className="px-6 py-4 border-b border-luxury-border flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-luxury-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search by filename or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 border border-luxury-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-luxury-gold/30 w-full bg-luxury-elevated text-luxury-text placeholder-luxury-muted"
            />
          </div>
          {(searchQuery || filterCategory) && (
            <button
              onClick={() => { setSearchQuery(""); setFilterCategory(""); }}
              className="text-xs font-semibold px-3 py-2 rounded-xl transition text-luxury-secondary hover:bg-luxury-elevated hover:text-luxury-text"
            >
              Clear filters
            </button>
          )}
          <div className="ml-auto text-xs font-medium text-luxury-muted">
            {filteredDocs.length} of {documents.length} shown
          </div>
        </div>

        {/* Table headers */}
        {filteredDocs.length > 0 && (
          <div className="hidden md:grid grid-cols-12 px-6 py-3 text-xs font-semibold uppercase tracking-wider bg-luxury-elevated text-luxury-muted">
            <div className="col-span-5">Document</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-2">Tax Year</div>
            <div className="col-span-2">Size / Date</div>
            <div className="col-span-1"></div>
          </div>
        )}

        {/* Rows */}
        {loading ? (
          <div className="p-8 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-14 rounded-xl animate-pulse bg-luxury-elevated" />
            ))}
          </div>
        ) : filteredDocs.length === 0 ? (
          <div className="p-16 text-center">
            <div className="w-20 h-20 rounded-2xl mx-auto flex items-center justify-center mb-4 bg-luxury-elevated">
              <svg className="w-10 h-10 text-luxury-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold mb-2 text-luxury-text">
              {searchQuery || filterCategory ? "No documents match your filter" : "No documents yet"}
            </h3>
            <p className="text-sm mb-6 text-luxury-secondary">
              {searchQuery || filterCategory
                ? "Try clearing your search or selecting a different category."
                : "Upload your first tax document to get started."}
            </p>
            {!searchQuery && !filterCategory && (
              <button
                onClick={() => navigate("/upload")}
                className="px-6 py-2.5 bg-luxury-cta text-white rounded-xl font-semibold hover:bg-luxury-cta-hover transition active:scale-95 text-sm"
              >
                Upload First Document
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-luxury-border">
            {filteredDocs.map((doc) => (
              <div
                key={doc.id}
                className="grid grid-cols-1 md:grid-cols-12 items-center gap-3 px-6 py-4 transition group hover:bg-luxury-elevated"
              >
                {/* File + name */}
                <div className="col-span-5 flex items-center gap-3 min-w-0">
                  <FileIconBox fileType={doc.file_type} />
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate text-luxury-text">{doc.original_filename}</p>
                    {doc.description && (
                      <p className="text-xs truncate mt-0.5 text-luxury-secondary">{doc.description}</p>
                    )}
                  </div>
                </div>

                {/* Category */}
                <div className="col-span-2">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${CATEGORY_PILL[doc.category] || "bg-luxury-elevated text-luxury-secondary border border-luxury-border"}`}>
                    {CATEGORY_LABELS[doc.category] || doc.category}
                  </span>
                </div>

                {/* Tax Year */}
                <div className="col-span-2">
                  <span className={`text-sm ${doc.tax_year ? "text-luxury-text" : "text-luxury-muted"}`}>
                    {doc.tax_year || "—"}
                  </span>
                </div>

                {/* Size + Date */}
                <div className="col-span-2">
                  <p className="text-sm text-luxury-secondary">{formatFileSize(doc.file_size)}</p>
                  <p className="text-xs mt-0.5 text-luxury-muted">{formatDate(doc.created_at)}</p>
                </div>

                {/* Delete */}
                <div className="col-span-1 flex justify-end">
                  <button
                    onClick={() => handleDelete(doc.id)}
                    disabled={deletingId === doc.id}
                    className="p-2 rounded-lg transition opacity-0 group-hover:opacity-100 text-luxury-muted hover:text-red-400 hover:bg-red-900/20"
                    title="Delete document"
                  >
                    {deletingId === doc.id ? (
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
